import { Box, Grid, Typography, Button, Alert } from '@mui/material'
import { ChevronLeft } from '@mui/icons-material'
import { useEffect, useMemo, useState } from 'react'
import FileUploadService from '../../../../../../shared/services/file-upload/file-upload.service'
import { DataImportMappingTile } from '../../../../../presentation-components/settings-components/data-import/data-import-mapping-tile'
import { MappingSelection, createCompositeSelection, createHeaderSelection, isCompositeSelection, isConstantSelection, isHeaderSelection } from '../../../../../../shared/utils/mapping-selection.utils'

// Header/field comparison key: trim, lowercase, drop everything but letters and digits, so
// "Invoice Date", "invoice_date" and "InvoiceDate " all compare equal.
const normaliseForMatch = (value: string): string => String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '')

// Small, deliberately conservative synonym map for auto-mapping: normalised system field name to
// the normalised spreadsheet headings that obviously mean the same thing. Anything ambiguous stays
// out; the user can always map by hand.
const HEADER_SYNONYMS: { [normalisedSystemField: string]: string[] } = {
  unitsconsumed: ['qty', 'quantity', 'units', 'unit', 'usage', 'consumption'],
  unitsconsumedkgco2e: ['unitsconsumed', 'qty', 'quantity', 'kgco2e', 'emissionskgco2e'],
  total: ['amount', 'value', 'cost', 'spend', 'totalamount', 'netamount', 'grossamount'],
  invoicedate: ['date', 'transactiondate', 'billdate'],
  startdate: ['datefrom', 'fromdate', 'periodstart'],
  enddate: ['dateto', 'todate', 'periodend'],
  supplier: ['vendor', 'suppliername', 'vendorname'],
  invoicenumber: ['invoiceno', 'invno'],
  reference: ['ref'],
  currency: ['currencycode'],
  nominalcode: ['glcode', 'accountcode'],
  energyconsumptionkwh: ['kwh', 'energykwh', 'energyconsumption'],
}

export const CustomMappingStep = ({ uploadId, onComplete, onBack }: { uploadId: string; onComplete: (result: any) => void; onBack: () => void }) => {
  //Services
  const fileUploadService = new FileUploadService();
  //State
  const [fileUploadMapped, setFileUploadMapped] = useState<any>(null)
  const [headings, setHeadings] = useState<string[]>([])
  const [gaiaFields, setGaiaFields] = useState<string[]>([])
  const [mapping, setMapping] = useState<{ [gaiaField: string]: MappingSelection[] }>({})
  const [autoMappedCount, setAutoMappedCount] = useState<number>(0)
  const [error, setError] = useState<string | null>(null)
  //Loading State
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const hasMappedSelection = (selections?: MappingSelection[]) => {
    if (!selections || selections.length === 0) return false
    return selections.some(selection => {
      if (isHeaderSelection(selection)) return selection.value.trim() !== ''
      if (isConstantSelection(selection)) return Boolean((selection.displayValue ?? selection.value)?.toString().trim())
      if (isCompositeSelection(selection)) return true
      return false
    })
  }

  const mandatoryFields = useMemo<string[]>(() => {
    const opts = (fileUploadMapped?.options || []) as Array<any>
    return opts.filter(o => String(o.status).toLowerCase() === 'mandatory').map(o => o.name)
  }, [fileUploadMapped])
  const missingMandatory = useMemo<string[]>(() => {
    return mandatoryFields.filter(field => !hasMappedSelection(mapping[field]))
  }, [mandatoryFields, mapping])

  useEffect(() => {
    setIsLoading(true)
    setError(null)
    fileUploadService.getUploadFileForMapping(uploadId)
      .then((response: any) => {
        if (response.code !== undefined) {
          setError(response.message || 'An error occurred while loading the mapping data')
          setFileUploadMapped(null)
          setHeadings([])
          setGaiaFields([])
          setMapping({})
          setAutoMappedCount(0)
          return
        }
        setFileUploadMapped(response)
        setHeadings(response.headers || [])
        setGaiaFields((response.options || []).map((o: any) => o.name))
        // Seed the mapping. A previously saved supplier mapping always takes precedence; any field
        // it leaves unmapped is auto-filled when a spreadsheet heading matches the field name after
        // normalisation, or matches one of a few obvious synonyms. Auto-filled values stay editable.
        const seeded: { [gaiaField: string]: MappingSelection[] } = {}
        if (response.contactSupplier?.mappings?.length > 0) {
          response.contactSupplier.mappings.forEach((m: { customHeading: string; mappedHeading: string }) => {
            if (!seeded[m.mappedHeading]) seeded[m.mappedHeading] = []
            seeded[m.mappedHeading].push(createHeaderSelection(m.customHeading))
          })
        }
        const headingByKey = new Map<string, string>()
        for (const heading of (response.headers || []) as string[]) {
          const key = normaliseForMatch(heading)
          if (key && !headingByKey.has(key)) headingByKey.set(key, heading)
        }
        const usedHeadings = new Set<string>(
          Object.values(seeded).flat().filter(isHeaderSelection).map(selection => selection.value)
        )
        let autoMatched = 0
        const claimHeading = (fieldName: string, heading: string) => {
          seeded[fieldName] = [createHeaderSelection(heading)]
          usedHeadings.add(heading)
          autoMatched += 1
        }
        // Composite fields sync child fields through their own change handler, so they are left to
        // the user; every plain field is fair game.
        const autoMappableOptions = ((response.options || []) as any[])
          .filter(option => !seeded[option.name]?.length && String(option.fieldType).toLowerCase() !== 'composite')
        // Exact name matches first, then synonyms: an exact match is the stronger signal, and each
        // heading is claimed at most once so one column never quietly fills two fields.
        const stillUnmatched: any[] = []
        for (const option of autoMappableOptions) {
          const exact = headingByKey.get(normaliseForMatch(option.name))
          if (exact && !usedHeadings.has(exact)) {
            claimHeading(option.name, exact)
          } else {
            stillUnmatched.push(option)
          }
        }
        for (const option of stillUnmatched) {
          for (const synonym of (HEADER_SYNONYMS[normaliseForMatch(option.name)] || [])) {
            const heading = headingByKey.get(synonym)
            if (heading && !usedHeadings.has(heading)) {
              claimHeading(option.name, heading)
              break
            }
          }
        }
        setAutoMappedCount(autoMatched)
        if (Object.keys(seeded).length > 0) {
          setMapping(seeded)
        }
      })
      .catch((err: any) => {
        setError(err?.response?.data?.message || err?.message || 'An error occurred while loading the mapping data')
        setFileUploadMapped(null)
        setHeadings([])
        setGaiaFields([])
        setMapping({})
        setAutoMappedCount(0)
      })
      .finally(() => setIsLoading(false))
  }, [uploadId])

  const getCompositeChildren = (gaiaField: string): string[] => {
    const meta = (fileUploadMapped?.options || []).find((o: any) => o.name === gaiaField)
    if (!meta) return []
    if (String(meta.fieldType).toLowerCase() !== 'composite') return []
    const struct = meta.fieldStructure
    if (Array.isArray(struct)) {
      return struct.map((s: any) => String(s).trim()).filter(Boolean)
    }
    if (typeof struct === 'string') {
      return struct.split(',').map((s: string) => s.trim()).filter(Boolean)
    }
    if (struct != null) {
      try {
        const parsed = JSON.parse(String(struct))
        if (Array.isArray(parsed)) {
          return parsed.map((s: any) => String(s).trim()).filter(Boolean)
        }
      } catch {
        return []
      }
    }
    return []
  }

  const handleMappingChange = (gaiaField: string, selections: MappingSelection[] = []) => {
    const arr = Array.isArray(selections) ? selections : []
    const compositeChildren = getCompositeChildren(gaiaField)
    setMapping(prev => {
      const next = { ...prev }
      if (!arr.length) {
        delete next[gaiaField]
      } else {
        next[gaiaField] = arr
      }
      if (compositeChildren.length > 0) {
        if (!arr.length) {
          compositeChildren.forEach(child => {
            const current = next[child]
            if (!current) return
            const filtered = current.filter(selection => !(isCompositeSelection(selection) && selection.parentField === gaiaField))
            if (!filtered.length) {
              delete next[child]
            } else {
              next[child] = filtered
            }
          })
        } else {
          compositeChildren.forEach(child => {
            next[child] = [createCompositeSelection(gaiaField)]
          })
        }
      }
      return next
    })
  }

  const handleSubmit = async () => {
    if (missingMandatory.length > 0) return
    setIsSubmitting(true)
    try {
      const mappingArray: { customHeading: string; mappedHeading: string; isGlobalValue?: boolean }[] = []
      for (const gaiaField in mapping) {
        const selections = mapping[gaiaField] || []
        selections.forEach(selection => {
          if (isCompositeSelection(selection)) return
          if (isConstantSelection(selection)) {
            const customHeading = selection.value?.toString()
            if (!customHeading) return
            mappingArray.push({ customHeading, mappedHeading: gaiaField, isGlobalValue: true })
            return
          }
          if (isHeaderSelection(selection)) {
            mappingArray.push({ customHeading: selection.value, mappedHeading: gaiaField })
          }
        })
      }
      const result = await fileUploadService.uploadFileMappings({ fileUploadId: uploadId, mappings: mappingArray })
      onComplete(result)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading mapping...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="h6" sx={{ mb: 2, textAlign: 'left' }}>
        Map columns
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!error && (
        <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', textAlign: 'left' }}>
          Match columns from your spreadsheet to system fields.
        </Typography>
      )}
      {fileUploadMapped?.contactSupplier?.name && fileUploadMapped.contactSupplier.mappings.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Using mappings for Supplier: <strong>{fileUploadMapped.contactSupplier.name}</strong>
          </Typography>
        </Box>
      )}
      {fileUploadMapped && autoMappedCount > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {autoMappedCount} of {gaiaFields.length} fields matched automatically. Check them and map the rest.
          </Typography>
        </Box>
      )}
      {fileUploadMapped && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Required fields</Typography>
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {gaiaFields
              .filter((f) => (fileUploadMapped.options || []).find((o: any) => o.name === f)?.status === 'mandatory')
              .map((gaiaField: string, index: number) => (
                <Grid item lg={4} md={6} sm={12} xs={12} key={`req-${index}`}>
                  <DataImportMappingTile
                    value={mapping[gaiaField] || []}
                    selectedValues={mapping}
                    header={gaiaField}
                    fileUpload={fileUploadMapped}
                    handleChange={handleMappingChange}
                    optionsOverride={headings.map(h => ({ name: h, label: 'header', status: 'optional', fieldType: 'header', fieldStructure: '', allowMultiple: true }))}
                    multiple={false}
                  />
                </Grid>
              ))}
          </Grid>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Optional fields</Typography>
          <Grid container spacing={4}>
            {gaiaFields
              .filter((f) => (fileUploadMapped.options || []).find((o: any) => o.name === f)?.status !== 'mandatory')
              .map((gaiaField: string, index: number) => (
                <Grid item lg={4} md={6} sm={12} xs={12} key={`opt-${index}`}>
                  <DataImportMappingTile
                    value={mapping[gaiaField] || []}
                    selectedValues={mapping}
                    header={gaiaField}
                    fileUpload={fileUploadMapped}
                    handleChange={handleMappingChange}
                    optionsOverride={headings.map(h => ({ name: h, label: 'header', status: 'optional', fieldType: 'header', fieldStructure: '', allowMultiple: true }))}
                    multiple={false}
                  />
                </Grid>
              ))}
          </Grid>
        </>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={onBack} startIcon={<ChevronLeft />} sx={{ minWidth: 100 }}>
          <Typography>
            Back
          </Typography>
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          {!error && missingMandatory.length > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right' }}>
              Still needed: {missingMandatory.join(', ')}
            </Typography>
          )}
          <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting || missingMandatory.length > 0 || !!error} sx={{ minWidth: 100, flexShrink: 0 }}>
            <Typography>
              {isSubmitting ? 'Saving...' : 'Next'}
            </Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  )
}


